import React from 'react';
import yaml from 'js-yaml';
import {
  Alert,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  BoxProps,
  Icon,
  Text,
  Flex,
  Switch,
  Heading,
} from 'pounceV2';
import SchemaDescriptorV3 from 'Components/SchemaDescriptor/SchemaDescriptorV3';
import EditorV3 from 'Components/Editor/EditorV3';
import LinkifyV3 from 'Components/Linkify/LinkifyV3';
import { mergeYamlSchemasWithDiscovery } from 'Helpers/utils';
import { DataSchemaFull } from 'Source/graphql/fragments/DataSchemaFull.generated';
import { useTheme } from '@mui/material';

enum EditorViewEnum {
  TREE = 'Tree',
  YAML = 'YAML',
}

export interface SchemaViewerProps extends BoxProps {
  schema: DataSchemaFull;
  withDescription?: boolean;
  onIndicatorsChange?: (fieldPath: string, indicators: string[]) => void;
}

const SchemaViewerV3 = ({
  schema,
  withDescription,
  onIndicatorsChange,
  ...rest
}: SchemaViewerProps) => {
  const {
    palette: { tokens },
  } = useTheme();
  const [editorView, setEditorView] = React.useState<EditorViewEnum>(EditorViewEnum.TREE);
  const handleEditorView = (event: React.MouseEvent<HTMLElement>, value: EditorViewEnum) => {
    setEditorView(value);
  };
  const [showDiscoveriedFields, setShowDiscoveriedFields] = React.useState<boolean>(true);

  const mergedSchema = React.useMemo(() => {
    if (!schema.isFieldDiscoveryEnabled) {
      return schema.spec;
    }
    return mergeYamlSchemasWithDiscovery(schema.spec, schema.discoveredSpec);
  }, [schema]);

  // Tree view follows the "Show Discovered Fields" toggle; YAML always shows the spec.
  const treeSpec = showDiscoveriedFields ? mergedSchema : schema.spec;

  // Empty-state when the tree would render no fields: empty/merged spec, or
  // discovered fields hidden by the toggle.
  const treeHasNoFields = React.useMemo(() => {
    try {
      const parsed = yaml.load(treeSpec) as { fields?: unknown[] } | undefined;
      return !parsed?.fields || parsed.fields.length === 0;
    } catch {
      return false;
    }
  }, [treeSpec]);

  return (
    <Box
      position="relative"
      borderRadius="medium"
      backgroundColor={tokens.background.neutral.inverse.raised.base}
      border={`1px solid ${tokens.stroke.neutral[5]}`}
      {...rest}
    >
      <Flex justify="flex-end" align="center" gap={3}>
        {editorView === EditorViewEnum.TREE && schema.isFieldDiscoveryEnabled && (
          <Switch
            checked={showDiscoveriedFields}
            onChange={event => {
              const isChecked = event.target.checked;
              setShowDiscoveriedFields(isChecked);
            }}
            label="Show Discovered Fields"
            data-tid="toggle-enable-switch"
          />
        )}
        <Box px={2} py={4}>
          <ToggleButtonGroup onChange={handleEditorView} value={editorView}>
            <ToggleButton
              value={EditorViewEnum.TREE}
              variant="secondary"
              size="medium"
              aria-label="Tree View"
            >
              <Icon type="tree-structure" />
            </ToggleButton>
            <ToggleButton
              value={EditorViewEnum.YAML}
              variant="secondary"
              size="medium"
              aria-label="YAML View"
            >
              <Icon type="code-structure" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Flex>
      <Box
        py={4}
        sx={{
          '.react-monaco-editor-container': {
            borderRadius: '6px',
            '.monaco-editor': {
              borderRadius: '6px',
              '.overflow-guard': {
                borderRadius: '6px',
              },
            },
          },
        }}
      >
        {editorView === EditorViewEnum.TREE && treeHasNoFields && (
          <Flex justify="center" align="center" px={6} py={6} whiteSpace="normal">
            <Box maxWidth={480} width="100%">
              <Alert
                variant="info"
                variantBackgroundStyle="solid"
                title="No fields configured"
                description={
                  schema.isFieldDiscoveryEnabled
                    ? "This schema doesn't have any fields defined. Fields will appear here as they're discovered from incoming data, or you can add them by editing the schema."
                    : "This schema doesn't have any fields defined. Edit the schema to add fields."
                }
              />
            </Box>
          </Flex>
        )}
        {editorView === EditorViewEnum.TREE && !treeHasNoFields && (
          <SchemaDescriptorV3
            yamlSpec={treeSpec}
            onIndicatorsChange={onIndicatorsChange}
            title={
              withDescription ? (
                // Caller passes whiteSpace="nowrap"; override so the description wraps.
                <Box mt={2} mb={4} ml={5} whiteSpace="normal">
                  <Heading variant="h6" fontWeight="medium" mb={1}>
                    {schema.description || 'No schema description'}
                  </Heading>

                  {schema.referenceURL ? (
                    <Text fontStyle="italic" color={tokens.content.text.accent[14][1]}>
                      <LinkifyV3>{schema.referenceURL}</LinkifyV3>
                    </Text>
                  ) : (
                    <Text fontStyle="italic" color={tokens.content.text.accent[14][1]}>
                      No schema reference link
                    </Text>
                  )}
                </Box>
              ) : null
            }
          />
        )}
        {editorView === EditorViewEnum.YAML && (
          <EditorV3 readOnly language="yaml" data-testid="schema-editor" value={schema.spec} />
        )}
      </Box>
    </Box>
  );
};

export default SchemaViewerV3;
